import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import { Card, CardBody, CardHeader, Badge, Spinner } from 'reactstrap';

const mapContainerStyle = { height: '72vh', width: '100%' };
const defaultCenter = { lat: 25.204849, lng: 55.270783 };

const TECH_COLOR_PALETTE = [
  '#E91E63', '#FFC107', '#A0522D', '#4CAF50', '#9C27B0',
  '#03A9F4', '#FF5722', '#673AB7', '#009688', '#795548',
  '#3F51B5', '#FF9800', '#00BCD4', '#8BC34A', '#F44336',
  '#2196F3', '#CDDC39', '#607D8B', '#E040FB', '#FF4081',
];

const UNASSIGNED_KEY = '__unassigned__';
const UNASSIGNED_COLOR = '#9E9E9E';
const UNASSIGNED_ALERT_COLOR = '#DC3545';

const PULSE_STYLE = `
@keyframes oms-bell-shake {
  0%, 50%, 100% { transform: rotate(0deg); }
  10%, 30%      { transform: rotate(-12deg); }
  20%, 40%      { transform: rotate(12deg); }
}
.oms-alert-icon {
  display: inline-block;
  transform-origin: 50% 20%;
  animation: oms-bell-shake 2.6s ease-in-out infinite;
}
`;

const GREYSCALE_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b6b6b' }],
  },
  {
    featureType: 'administrative.neighborhood',
    stylers: [{ visibility: 'off' }],
  },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#e8e8e8' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9a9a9a' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#e2e2e2' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7c7c7c' }],
  },
  {
    featureType: 'road.local',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bdbdbd' }],
  },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#aebcc4' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6f7e87' }],
  },
];

const STATUS_STYLES = {
  new: { bg: 'bg-soft-info', fg: 'text-info', icon: 'mdi-star-circle-outline' },
  pending: { bg: 'bg-soft-warning', fg: 'text-warning', icon: 'mdi-information-outline' },
  canceled: { bg: 'bg-soft-danger', fg: 'text-danger', icon: 'mdi-close-circle-outline' },
  resolved: { bg: 'bg-soft-success', fg: 'text-success', icon: 'mdi-check-circle-outline' },
  rescheduled: { bg: 'bg-soft-warning', fg: 'text-warning', icon: 'mdi-calendar-sync' },
  'assigned to technician': { bg: 'bg-soft-info', fg: 'text-info', icon: 'mdi-progress-clock' },
  'in progress': { bg: 'bg-soft-info', fg: 'text-info', icon: 'mdi-progress-clock' },
};

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const buildTechnicianMap = (orders) => {
  const map = new Map();
  let i = 0;
  for (const o of orders) {
    const key = o.emp_id ? String(o.emp_id) : UNASSIGNED_KEY;
    if (!map.has(key)) {
      const isUnassigned = key === UNASSIGNED_KEY;
      map.set(key, {
        key,
        color: isUnassigned
          ? UNASSIGNED_COLOR
          : TECH_COLOR_PALETTE[i % TECH_COLOR_PALETTE.length],
        label: o.emp_id ? o.technician_name || 'Technician' : 'Unassigned',
        empId: o.emp_id || '',
        count: 0,
      });
      if (!isUnassigned) i++;
    }
    map.get(key).count++;
  }
  return map;
};

const ServiceOrdersMapView = ({
  orders = [],
  isLoading = false,
  dateLabel = '',
  isDefaultDate = false,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_APP_KEY,
  });

  const [selected, setSelected] = useState(null);
  const [activeTechKey, setActiveTechKey] = useState(null);
  const mapRef = useRef(null);

  const mappableOrders = useMemo(
    () =>
      (orders || [])
        .map((o) => ({
          ...o,
          _lat: toNumber(o.lat ?? o.latitude),
          _lng: toNumber(o.lng ?? o.lon ?? o.longitude),
        }))
        .filter(
          (o) =>
            o._lat !== null &&
            o._lng !== null &&
            !(o._lat === 0 && o._lng === 0),
        ),
    [orders],
  );

  const techMap = useMemo(
    () => buildTechnicianMap(mappableOrders),
    [mappableOrders],
  );

  const visibleOrders = useMemo(() => {
    if (!activeTechKey) return mappableOrders;
    return mappableOrders.filter((o) => {
      const key = o.emp_id ? String(o.emp_id) : UNASSIGNED_KEY;
      return key === activeTechKey;
    });
  }, [mappableOrders, activeTechKey]);

  useEffect(() => {
    if (!mapRef.current || !window.google || !visibleOrders.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    visibleOrders.forEach((o) => bounds.extend({ lat: o._lat, lng: o._lng }));
    mapRef.current.fitBounds(bounds, {
      top: 60,
      right: 60,
      bottom: 60,
      left: 60,
    });
    const listener = window.google.maps.event.addListenerOnce(
      mapRef.current,
      'idle',
      () => {
        if (mapRef.current.getZoom() > 14) mapRef.current.setZoom(14);
      },
    );
    return () => window.google.maps.event.removeListener(listener);
  }, [visibleOrders, isLoaded]);

  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  const skipped = (orders?.length || 0) - mappableOrders.length;
  const sortedTechs = useMemo(
    () => Array.from(techMap.values()).sort((a, b) => b.count - a.count),
    [techMap],
  );

  if (loadError) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="text-danger">Map failed to load.</CardBody>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <style>{PULSE_STYLE}</style>
      <CardHeader className="bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
        <div className="d-flex align-items-center gap-2">
          <i
            className="mdi mdi-map-marker-radius-outline fs-3"
            style={{ color: '#3F51B5' }}
          ></i>
          <div>
            <h5 className="mb-0 fw-bold">Service Order Locations</h5>
            <div className="fs-11 text-muted">
              Each colored bubble represents an assigned job
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {dateLabel && (
            <Badge
              color={isDefaultDate ? 'soft-primary' : 'soft-info'}
              className={`d-inline-flex align-items-center gap-1 fs-12 px-2 py-2 border ${
                isDefaultDate
                  ? 'bg-soft-primary text-primary border-primary'
                  : 'bg-soft-info text-info border-info'
              }`}
            >
              <i className="mdi mdi-calendar-range-outline"></i>
              {dateLabel}
            </Badge>
          )}
          <Badge
            color="light"
            className="text-dark border d-inline-flex align-items-center gap-1 fs-12 px-2 py-2"
          >
            <i className="mdi mdi-map-marker-multiple-outline"></i>
            {visibleOrders.length} shown
          </Badge>
          <Badge
            color="light"
            className="text-dark border d-inline-flex align-items-center gap-1 fs-12 px-2 py-2"
          >
            <i className="mdi mdi-account-hard-hat-outline"></i>
            {techMap.size} technician{techMap.size !== 1 ? 's' : ''}
          </Badge>
          {skipped > 0 && (
            <Badge
              color="warning"
              className="d-inline-flex align-items-center gap-1 fs-12 px-2 py-2"
            >
              <i className="mdi mdi-map-marker-off-outline"></i>
              {skipped} no GPS
            </Badge>
          )}
          {activeTechKey && (
            <button
              type="button"
              className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
              onClick={() => setActiveTechKey(null)}
            >
              <i className="mdi mdi-filter-remove-outline"></i>
              Clear filter
            </button>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="d-flex flex-wrap" style={{ minHeight: '72vh' }}>
          <div
            style={{
              flex: '1 1 600px',
              position: 'relative',
              minHeight: '72vh',
            }}
          >
            {isLoaded && isLoading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.55)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 5,
                  backdropFilter: 'blur(1px)',
                  WebkitBackdropFilter: 'blur(1px)',
                }}
              >
                <div className="bg-white shadow rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2 border">
                  <Spinner size="sm" color="dark" />
                  <span className="fs-12 fw-semibold text-dark">
                    Loading orders…
                  </span>
                </div>
              </div>
            )}
            {!isLoaded ? (
              <div
                className="d-flex justify-content-center align-items-center h-100 bg-light"
                style={{ minHeight: '72vh' }}
              >
                <div className="text-muted">
                  <i className="mdi mdi-loading mdi-spin me-2"></i>Loading map…
                </div>
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={9}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  clickableIcons: false,
                  styles: GREYSCALE_MAP_STYLE,
                }}
                onLoad={onMapLoad}
              >
                {visibleOrders.map((o, idx) => {
                  const key = o.emp_id ? String(o.emp_id) : UNASSIGNED_KEY;
                  const color = techMap.get(key)?.color || UNASSIGNED_COLOR;
                  return (
                    <Marker
                      key={`${o.complaint_no || idx}-${o._lat}-${o._lng}`}
                      position={{ lat: o._lat, lng: o._lng }}
                      onClick={() => setSelected(o)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: color,
                        fillOpacity: 0.92,
                        strokeOpacity: 0,
                        strokeWeight: 0,
                      }}
                    />
                  );
                })}

                {selected && (
                  <InfoWindow
                    position={{ lat: selected._lat, lng: selected._lng }}
                    onCloseClick={() => setSelected(null)}
                    options={{
                      pixelOffset: new window.google.maps.Size(0, -8),
                    }}
                  >
                    <div style={{ minWidth: 260, padding: 4 }}>
                      <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                        <div>
                          <div className="fw-bold fs-13">
                            {selected.complaint_no}
                          </div>
                          {selected.service_order_no && (
                            <div className="fs-11 text-muted">
                              SO: {selected.service_order_no}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const status = (
                            selected.status_label || ''
                          ).toLowerCase();
                          const s = STATUS_STYLES[status] || {
                            bg: 'bg-soft-secondary',
                            fg: 'text-secondary',
                            icon: 'mdi-information-outline',
                          };
                          return (
                            <span
                              className={`badge ${s.bg} ${s.fg} d-inline-flex align-items-center gap-1 text-capitalize`}
                            >
                              <i className={`mdi ${s.icon}`}></i>
                              {selected.status_label || '—'}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="border-top pt-2">
                        <div className="fs-12 fw-semibold">
                          {selected.customer_name}
                        </div>
                        {selected.mobile && (
                          <div className="fs-11 text-muted">
                            <i className="mdi mdi-phone-outline me-1"></i>
                            {selected.mobile}
                          </div>
                        )}
                        {selected.address1 && (
                          <div
                            className="fs-11 text-muted mt-1"
                            style={{ lineHeight: 1.35 }}
                          >
                            <i className="mdi mdi-map-marker-outline me-1"></i>
                            {selected.address1}
                          </div>
                        )}
                      </div>
                      <div className="border-top pt-2 mt-2 d-flex flex-column gap-1 fs-11">
                        <div>
                          <i className="mdi mdi-account-hard-hat-outline me-1 text-muted"></i>
                          <b>Technician:</b>{' '}
                          {selected.emp_id ? (
                            `${selected.technician_name || ''} - ${selected.emp_id}`
                          ) : (
                            <span className="text-muted">Unassigned</span>
                          )}
                        </div>
                        {selected.service_type && (
                          <div className="text-capitalize">
                            <i className="mdi mdi-toolbox-outline me-1 text-muted"></i>
                            <b>Service:</b>{' '}
                            {String(selected.service_type).replace(/_/g, ' ')}
                          </div>
                        )}
                        {(selected.area || selected.emirate) && (
                          <div>
                            <i className="mdi mdi-pin-outline me-1 text-muted"></i>
                            <b>Area:</b> {selected.area || '-'}
                            {selected.emirate ? `, ${selected.emirate}` : ''}
                          </div>
                        )}
                      </div>
                      {selected.complaint_no && (
                        <a
                          href={`/service-orders/manage-service-orders/${selected.complaint_no}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-dark w-100 mt-3 d-inline-flex align-items-center justify-content-center gap-1"
                        >
                          <i className="mdi mdi-eye-outline"></i>
                          View Details
                        </a>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>

          <div
            className="border-start bg-white"
            style={{
              width: 280,
              maxHeight: '72vh',
              overflowY: 'auto',
              flexShrink: 0,
              flexGrow: 0,
            }}
          >
            <div
              className="px-3 py-2 border-bottom bg-light sticky-top"
              style={{ top: 0, zIndex: 1 }}
            >
              <div className="fw-semibold fs-13 d-flex align-items-center gap-1">
                <i className="mdi mdi-account-hard-hat-outline text-muted"></i>
                Technicians
              </div>
              <div className="fs-11 text-muted">Click to filter the map</div>
            </div>
            <div className="p-2 d-flex flex-column gap-1">
              {sortedTechs.length === 0 && (
                <div className="text-muted fs-12 p-3 text-center">
                  {isLoading ? (
                    <>
                      <i className="mdi mdi-loading mdi-spin me-1"></i>
                      Loading…
                    </>
                  ) : (
                    'No mappable orders.'
                  )}
                </div>
              )}
              {sortedTechs.map((t) => {
                const isActive = activeTechKey === t.key;
                const isDimmed = activeTechKey !== null && !isActive;
                const isUnassigned = t.key === UNASSIGNED_KEY;
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() =>
                      setActiveTechKey(isActive ? null : t.key)
                    }
                    className="btn btn-sm text-start d-flex align-items-center gap-2 px-2 py-2 border"
                    style={{
                      backgroundColor: isActive ? `${t.color}1A` : '#fff',
                      borderColor: isUnassigned
                        ? UNASSIGNED_ALERT_COLOR
                        : isActive
                        ? t.color
                        : '#e9ecef',
                      borderWidth: isActive || isUnassigned ? 2 : 1,
                      opacity: isDimmed ? 0.55 : 1,
                      transition: 'all 0.15s ease',
                      borderRadius: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: t.color,
                        flexShrink: 0,
                        border: isUnassigned
                          ? `2px solid ${UNASSIGNED_ALERT_COLOR}`
                          : '2px solid #fff',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                      }}
                    />
                    <div
                      className="flex-grow-1 text-truncate"
                      style={{ minWidth: 0 }}
                    >
                      <div
                        className={`fs-12 fw-semibold text-truncate d-flex align-items-center gap-1 ${
                          isUnassigned ? 'text-danger' : ''
                        }`}
                      >
                        {isUnassigned && (
                          <i className="mdi mdi-bell-ring-outline oms-alert-icon"></i>
                        )}
                        {t.label}
                      </div>
                      {t.empId && (
                        <div className="fs-10 text-muted">ID: {t.empId}</div>
                      )}
                      {isUnassigned && (
                        <div className="fs-10 text-danger">
                          Needs assignment
                        </div>
                      )}
                    </div>
                    <span
                      className={`badge border fw-bold ${
                        isUnassigned ? 'bg-danger text-white' : 'text-dark'
                      }`}
                      style={{
                        minWidth: 30,
                        backgroundColor: isUnassigned
                          ? undefined
                          : '#f8f9fa',
                      }}
                    >
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default ServiceOrdersMapView;
